import React, { createRef, forwardRef } from "react";

import Portal from "@stadium/ui/src/components/portal";

// eslint-disable-next-line import/no-extraneous-dependencies
import defaultStyles from "@stadium/ui/src/components/header/main.module.scss";

export const FEATURED_MENU_BAR_REF = createRef(null);

export const STICKY_HEADER_REF = createRef(null);

export const StickyHeader = ({
  styles = defaultStyles,
  children,
  rootClass = "",
  className = "",
}) => {
  return (
    <Portal
      id="sticky-header-root"
      rootClassName={`${styles.portalContainer} ${rootClass}`}
      dataContainerId="sticky-header"
      className={className}
    >
      {children}
    </Portal>
  );
};

const splitStickyHeaderEls = (elements) => {
  const arrayChildren = React.Children.toArray(elements);
  return arrayChildren.reduce(
    (elData, reactEl) => {
      if (reactEl.type === StickyHeader) {
        elData.stickyElement = reactEl;
      } else if (elData.stickyElement) {
        elData.elementsAfterSticky.push(reactEl);
      } else {
        elData.elementsBeforeSticky.push(reactEl);
      }
      return elData;
    },
    { elementsBeforeSticky: [], elementsAfterSticky: [], stickyElement: null },
  );
};

export const ResponsiveHeader = ({
  styles = defaultStyles,
  customClass = styles.responsive,
  children,
}) => <div className={`${styles.programResponsiveHeader} ${customClass}`}>{children}</div>;

export const NonResponsiveHeader = ({ styles = defaultStyles, customClass = "", children }) => (
  <div className={`${styles.programResponsiveHeader} ${styles.nonResponsive} ${customClass}`}>
    {children}
  </div>
);

export const ActivePageHeading = ({ styles = defaultStyles, title = "" }) => (
  <div className={styles.activeLinkContainer}>{title}</div>
);

export const HeaderContainer = ({
  styles = defaultStyles,
  children,
  baseClass = styles.headerWrapper,
  className = "",
}) => {
  const { elementsBeforeSticky, elementsAfterSticky, stickyElement } =
    splitStickyHeaderEls(children);

  const renderElements = (els) =>
    els.length ? <div className={`${baseClass} ${className}`}>{els} </div> : "";

  return (
    <>
      {renderElements(elementsBeforeSticky)}
      {stickyElement ? (
        <>
          <div id="sticky-header-root" ref={STICKY_HEADER_REF} className={styles.stickyTopHeader} />
          {stickyElement}
        </>
      ) : (
        ""
      )}
      {renderElements(elementsAfterSticky)}
    </>
  );
};

export const Header = ({
  styles = defaultStyles,
  children,
  baseClass = styles.headerBase,
  className = "",
  level,
}) => <div className={`${baseClass} ${className} level-${level}`}>{children}</div>;

export const ResponsiveHeaderContainer = ({
  styles = defaultStyles,
  children,
  baseClass = styles.responsiveHeaderWrapper,
  className = "",
}) => {
  const { elementsBeforeSticky, elementsAfterSticky, stickyElement } =
    splitStickyHeaderEls(children);

  const renderElements = (els) =>
    els.length ? <div className={`${baseClass} ${className}`}>{els} </div> : "";

  return (
    <>
      {renderElements(elementsBeforeSticky)}
      {stickyElement ? (
        <>
          <div id="sticky-header-root" ref={STICKY_HEADER_REF} />
          {stickyElement}
        </>
      ) : (
        ""
      )}
      {renderElements(elementsAfterSticky)}
    </>
  );
};

export const ResponsiveHeaderContent = forwardRef(
  (
    {
      styles = defaultStyles,
      baseClass = styles.programResponsiveHeader,
      className = "",
      children,
      level,
    },
    ref,
  ) => (
    <div className={`${baseClass} ${className} level-${level}`} ref={ref}>
      {children}
    </div>
  ),
);

export const HeaderLeftContent = ({
  styles = defaultStyles,
  className = "",
  baseClass = styles.headerLeftContent,
  children,
}) => <div className={`${baseClass} ${className}`}>{children}</div>;

export const HeaderCenterContent = ({
  styles = defaultStyles,
  className = "",
  baseClass = styles.headerCenterCol,
  children,
}) => <div className={`${baseClass} ${className}`}>{children}</div>;

export const HeaderRightContent = ({
  styles = defaultStyles,
  className = "",
  baseClass = styles.headerRightContent,
  children,
}) => <div className={`${baseClass} ${className}`}>{children}</div>;

export const HeaderContent = ({
  styles = defaultStyles,
  className = "",
  baseClass = styles.headerContent,
  level,
  children,
}) => <div className={`${baseClass} ${className} level-${level}`}>{children}</div>;
